// Google Analytics 4 Integration Service
// Handles all GA4 event tracking and page views

class GA4Analytics {
  constructor() {
    const measurementId = (process.env.REACT_APP_GA4_MEASUREMENT_ID || '').trim();
    this.measurementId = measurementId;
    this.isEnabled = this.isValidMeasurementId(measurementId);
    this.initialized = false;
    
    if (this.isEnabled) {
      this.init();
    } else {
      if (process.env.NODE_ENV === 'development') {
        console.warn('GA4 Analytics not initialized - missing or invalid REACT_APP_GA4_MEASUREMENT_ID');
      }
    }
  }

  isValidMeasurementId(measurementId) {
    return /^G-[A-Z0-9]+$/i.test(measurementId);
  }

  // Initialize GA4
  init() {
    if (typeof window === 'undefined') {
      return;
    }

    window.dataLayer = window.dataLayer || [];
    window.gtag = window.gtag || function gtag(){ window.dataLayer.push(arguments); };

    const scriptSrc = `https://www.googletagmanager.com/gtag/js?id=${this.measurementId}`;
    const hasScript = typeof document !== 'undefined' &&
      document.querySelector(`script[src="${scriptSrc}"]`);

    if (!hasScript && typeof document !== 'undefined') {
      const script = document.createElement('script');
      script.async = true;
      script.src = scriptSrc;
      document.head.appendChild(script);
    }

    window.gtag('js', new Date());
    window.gtag('config', this.measurementId, {
      send_page_view: false
    });
    this.initialized = true;

    if (process.env.NODE_ENV === 'development') {
      console.log('GA4 Analytics initialized with ID:', this.measurementId);
    }
  }

  // Send page view
  pageView(page_title, page_location) {
    if (this.isEnabled && !this.initialized) this.init();
    if (!this.isEnabled || !window.gtag) return;

    window.gtag('event', 'page_view', {
      page_title: page_title,
      page_location: page_location || window.location.href,
      page_path: window.location.pathname
    });

    if (process.env.NODE_ENV === 'development') {
      console.log('GA4 Page View:', {
        page_title,
        page_location: page_location || window.location.href,
        page_path: window.location.pathname
      });
    }
  }

  // Track custom events
  event(action, parameters = {}) {
    if (this.isEnabled && !this.initialized) this.init();
    if (!this.isEnabled || !window.gtag) return;

    window.gtag('event', action, {
      ...parameters,
      timestamp: new Date().toISOString()
    });

    if (process.env.NODE_ENV === 'development') {
      console.log('GA4 Event:', action, parameters);
    }
  }

  // Track conversions
  conversion(action, value = 0, currency = 'USD', parameters = {}) {
    if (this.isEnabled && !this.initialized) this.init();
    if (!this.isEnabled || !window.gtag) return;

    window.gtag('event', action, {
      value: value,
      currency: currency,
      ...parameters
    });

    if (process.env.NODE_ENV === 'development') {
      console.log('GA4 Conversion:', action, { value, currency, ...parameters });
    }
  }

  // Track ecommerce events (for quote values)
  ecommerce(action, items = [], transaction_id = null, value = 0) {
    if (this.isEnabled && !this.initialized) this.init();
    if (!this.isEnabled || !window.gtag) return;

    const ecommerce_data = {
      currency: 'USD',
      value: value,
      items: items
    };

    if (transaction_id) {
      ecommerce_data.transaction_id = transaction_id;
    }

    window.gtag('event', action, ecommerce_data);

    if (process.env.NODE_ENV === 'development') {
      console.log('GA4 Ecommerce:', action, ecommerce_data);
    }
  }

  // Set user properties
  setUserProperties(properties = {}) {
    if (this.isEnabled && !this.initialized) this.init();
    if (!this.isEnabled || !window.gtag) return;

    window.gtag('config', this.measurementId, {
      custom_map: properties
    });
  }

  // Set user ID for cross-platform tracking
  setUserId(userId) {
    if (this.isEnabled && !this.initialized) this.init();
    if (!this.isEnabled || !window.gtag) return;

    window.gtag('config', this.measurementId, {
      user_id: userId
    });

    if (process.env.NODE_ENV === 'development') {
      console.log('GA4 User ID set:', userId);
    }
  }
}

// Pre-defined tracking functions for common events
export const ga4 = new GA4Analytics();

// Page tracking
export const trackPageView = (title) => {
  ga4.pageView(title);
};

// User journey tracking
export const trackSignupStart = () => {
  ga4.event('sign_up_start', {
    method: 'email'
  });
};

export const trackSignupComplete = (method = 'email') => {
  ga4.conversion('sign_up', 0, 'USD', {
    method: method
  });
};

export const trackSignupError = (reason = 'unknown') => {
  ga4.event('sign_up_error', {
    method: 'email',
    reason
  });
};

export const trackLogin = (method = 'email') => {
  ga4.event('login', {
    method: method
  });
};

export const trackTrialStart = (plan = 'free_trial') => {
  ga4.conversion('trial_start', 0, 'USD', {
    subscription_plan: plan
  });
};

// Quote tracking
export const trackQuoteCreated = (quoteId, totalValue = 0) => {
  ga4.event('quote_created', {
    quote_id: quoteId,
    value: totalValue,
    currency: 'USD'
  });
};

export const trackQuoteLinkCopied = (quoteId) => {
  ga4.event('quote_link_copied', {
    quote_id: quoteId
  });
};

export const trackQuoteViewed = (quoteId, viewerType = 'client') => {
  ga4.event('quote_viewed', {
    quote_id: quoteId,
    viewer_type: viewerType
  });
};

export const trackQuoteAccepted = (quoteId, selectedTier, totalValue) => {
  // Track as conversion
  ga4.conversion('quote_accepted', totalValue, 'USD', {
    quote_id: quoteId,
    selected_tier: selectedTier
  });

  // Track as ecommerce purchase
  ga4.ecommerce('purchase', [
    {
      item_id: quoteId,
      item_name: `Quote ${quoteId}`,
      item_category: 'Service Quote',
      item_variant: selectedTier,
      quantity: 1,
      price: totalValue
    }
  ], quoteId, totalValue);
};

export const trackCheckoutStarted = (plan = 'pro') => {
  ga4.event('begin_checkout', {
    item_name: plan,
    currency: 'USD',
    value: plan === 'pro' ? 29 : 0
  });
};

export const trackCheckoutCompleted = (plan = 'pro', transactionId = null) => {
  ga4.conversion('purchase', plan === 'pro' ? 29 : 0, 'USD', {
    item_name: plan,
    transaction_id: transactionId || undefined
  });
};

export const trackFreePlanSelected = () => {
  ga4.conversion('free_plan_selected', 0, 'USD', {
    plan: 'free'
  });
};

// Business actions
export const trackDashboardVisit = () => {
  ga4.event('dashboard_visit');
};

export const trackProductCreated = (productType) => {
  ga4.event('product_created', {
    product_type: productType
  });
};

export const trackEmailSent = (emailType, recipientType = 'client') => {
  ga4.event('email_sent', {
    email_type: emailType,
    recipient_type: recipientType
  });
};

// Engagement tracking
export const trackButtonClick = (buttonName, location) => {
  ga4.event('click', {
    button_name: buttonName,
    location: location
  });
};

export const trackFormSubmit = (formName) => {
  ga4.event('form_submit', {
    form_name: formName
  });
};

export const trackSearchPerformed = (searchTerm) => {
  ga4.event('search', {
    search_term: searchTerm
  });
};

// Error tracking
export const trackError = (errorMessage, errorLocation) => {
  ga4.event('exception', {
    description: errorMessage,
    location: errorLocation,
    fatal: false
  });
};

export default ga4;
